import * as stylex from '@stylexjs/stylex';

// Keyframes


const shimmer = stylex.keyframes({
  '0%': { left: '-100%' },
  '100%': { left: '100%' },
});

const shimmerSweep = stylex.keyframes({
  '0%': { left: '-100%' },
  '100%': { left: '100%' },
});

const MEDIA_1200 = '@media (min-width: 1200px)';
const MEDIA_992 = '@media (max-width: 992px)';
const MEDIA_768 = '@media (max-width: 768px)';
const MEDIA_480 = '@media (max-width: 480px)';

export const styles = stylex.create({
  /* Page Layout */
  examplesPage: {
    maxWidth: '100%',
    marginInline: 'auto',
    padding: 0,
  },

  /* Back to Home Link */
  backLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 'var(--spacing-sm)',
    color: {
      default: 'var(--text-secondary)',
      ':hover': 'var(--primary)',
    },
    textDecoration: 'none',
    fontSize: 'var(--font-size-sm)',
    marginBottom: 'var(--spacing-md)',
    transitionProperty: 'color',
    transitionDuration: 'var(--transition-fast)',
  },

  /* Page Header */
  pageHeader: {
    backgroundImage: `linear-gradient(135deg, 
            var(--bg-card) 0%, 
            rgba(59, 130, 246, 0.02) 15%, 
            rgba(16, 185, 129, 0.02) 35%, 
            rgba(255, 107, 107, 0.02) 55%, 
            rgba(139, 92, 246, 0.02) 75%, 
            var(--bg-card) 100%)`,
    borderRadius: 'var(--radius-xl)',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'var(--border)',
    padding: 'var(--spacing-2xl)',
    marginBottom: 'var(--spacing-3xl)',
    textAlign: 'center',
    position: 'relative',
    overflow: 'hidden',
    backdropFilter: 'blur(4px)',
    '::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundImage: `
        radial-gradient(circle at 15% 40%, rgba(59, 130, 246, 0.03) 0%, transparent 50%),
        radial-gradient(circle at 85% 60%, rgba(16, 185, 129, 0.03) 0%, transparent 50%),
        radial-gradient(circle at 40% 20%, rgba(255, 107, 107, 0.02) 0%, transparent 50%),
        radial-gradient(circle at 60% 80%, rgba(139, 92, 246, 0.02) 0%, transparent 50%)`,
      opacity: 1,
    },
    '::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundImage: `
        radial-gradient(circle at 70% 30%, rgba(255, 193, 7, 0.01) 0%, transparent 40%),
        radial-gradient(circle at 30% 70%, rgba(156, 39, 176, 0.01) 0%, transparent 40%)`,
      opacity: 0.8,
    },
  },

  headerContent: {
    position: 'relative',
    zIndex: 1,
  },

  pageTitle: {
    fontSize: 'var(--font-size-3xl)',
    fontWeight: 700,
    color: 'var(--text-primary)',
    marginBottom: 'var(--spacing-lg)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 'var(--spacing-md)',
  },

  titleIcon: {
    color: 'var(--primary)',
  },

  pageSubtitle: {
    fontSize: 'var(--font-size-lg)',
    color: 'var(--text-secondary)',
    maxWidth: '600px',
    marginInline: 'auto',
    lineHeight: 1.6,
  },

  /* Examples Section */
  examplesSection: {
    marginBottom: 'var(--spacing-3xl)',
  },

  sectionTitle: {
    fontSize: {
      default: 'var(--font-size-2xl)',
      [MEDIA_768]: 'var(--font-size-xl)',
      [MEDIA_480]: 'var(--font-size-lg)',
    },
    fontWeight: 600,
    color: 'var(--text-primary)',
    marginBottom: 'var(--spacing-xl)',
    textAlign: 'center',
    position: 'relative',
    '::after': {
      content: '""',
      position: 'absolute',
      bottom: '-8px',
      left: '50%',
      transform: 'translateX(-50%)',
      width: '60px',
      height: '3px',
      backgroundImage: 'linear-gradient(90deg, var(--primary), var(--accent))',
      borderRadius: 'var(--radius-full)',
    },
  },

  /* Examples Grid */
  examplesGrid: {
    display: 'grid',
    gridTemplateColumns: {
      default: 'repeat(auto-fit, minmax(350px, 1fr))',
      [MEDIA_1200]: 'repeat(3, 1fr)',
      [MEDIA_992]: 'repeat(auto-fit, minmax(300px, 1fr))',
      [MEDIA_768]: '1fr',
    },
    gap: {
      default: 'var(--spacing-xl)',
      [MEDIA_992]: 'var(--spacing-lg)',
      [MEDIA_768]: 'var(--spacing-lg)',
    },
    marginTop: {
      default: 'var(--spacing-xl)',
      [MEDIA_768]: 'var(--spacing-lg)',
    },
    maxWidth: '1200px',
    marginInline: 'auto',
  },

  /* Example Box */
  exampleBox: {
    backgroundColor: 'var(--bg-card)',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: {
      default: 'var(--border)',
      ':hover': 'var(--primary)',
    },
    borderRadius: 'var(--radius-lg)',
    overflow: 'hidden',
    position: 'relative',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    transitionProperty: 'transform, box-shadow, border-color',
    transitionDuration: 'var(--transition-normal)',
    transform: {
      default: 'none',
      ':hover': 'translateY(-4px)',
    },
    boxShadow: {
      default: 'none',
      ':hover': 'var(--shadow-lg)',
    },
    marginBottom: {
      default: 0,
      [MEDIA_768]: 'var(--spacing-md)',
    },
    '::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '4px',
      backgroundImage: 'linear-gradient(90deg, var(--primary), var(--accent))',
      transitionDuration: 'var(--transition-normal)',
      transform: {
        default: 'scaleX(0)',
        ':hover': 'scaleX(1)',
      },
    },
  },



  /* Video Container */
  videoContainer: {
    position: 'relative',
    width: '100%',
    height: {
      default: '200px',
      [MEDIA_768]: '180px',
    },
    overflow: 'hidden',
    backgroundColor: 'var(--bg-secondary)',
  },

  videoElement: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    transitionProperty: 'transform',
    transitionDuration: 'var(--transition-normal)',
  },

  videoElementHover: {
    transform: 'scale(1.05)',
  },

  categoryBadge: {
    position: 'absolute',
    top: 'var(--spacing-md)',
    right: 'var(--spacing-md)',
    backgroundColor: 'var(--primary)',
    color: 'var(--text-on-accent)',
    paddingBlock: 'var(--spacing-xs)',
    paddingInline: 'var(--spacing-sm)',
    borderRadius: 'var(--radius-full)',
    fontSize: 'var(--font-size-xs)',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    backdropFilter: 'blur(4px)',
    zIndex: 2,
  },

  /* Content */
  content: {
    padding: {
      default: 'var(--spacing-lg)',
      [MEDIA_768]: 'var(--spacing-md)',
    },
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },

  exampleTitle: {
    fontSize: 'var(--font-size-lg)',
    fontWeight: 600,
    color: 'var(--text-primary)',
    margin: '0 0 var(--spacing-sm) 0',
    lineHeight: 1.3,
  },

  exampleDescription: {
    color: 'var(--text-secondary)',
    margin: '0 0 var(--spacing-lg) 0',
    lineHeight: 1.6,
    fontSize: 'var(--font-size-sm)',
    flex: 1,
  },

  /* Metadata */
  metadata: {
    display: 'flex',
    gap: {
      default: 'var(--spacing-lg)',
      [MEDIA_768]: 'var(--spacing-md)',
    },
    flexWrap: 'wrap',
    marginBottom: 'var(--spacing-md)',
  },

  metadataItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-xs)',
    color: 'var(--text-tertiary)',
    fontSize: 'var(--font-size-sm)',
  },

  metadataIcon: {
    color: 'var(--primary)',
    flexShrink: 0,
  },

  metadataText: {
    fontWeight: 500,
  },

  /* Actions */
  actions: {
    marginTop: 'auto',
    paddingTop: 'var(--spacing-md)',
    borderTopWidth: '1px',
    borderTopStyle: 'solid',
    borderTopColor: 'var(--border)',
  },

  viewButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 'var(--spacing-sm)',
    backgroundColor: {
      default: 'var(--primary)',
      ':hover': 'var(--primary-hover)',
    },
    color: 'var(--text-on-accent)',
    paddingBlock: 'var(--spacing-sm)',
    paddingInline: 'var(--spacing-md)',
    borderRadius: 'var(--radius-md)',
    textDecoration: 'none',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 500,
    transitionProperty: 'all',
    transitionDuration: 'var(--transition-normal)',
    position: 'relative',
    overflow: 'hidden',
    transform: {
      default: 'none',
      ':hover': 'translateY(-1px)',
      ':active': 'translateY(0)',
    },
    boxShadow: {
      default: 'none',
      ':hover': 'var(--shadow-md)',
    },
    '::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: '-100%',
      width: '100%',
      height: '100%',
      backgroundImage: 'linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.3) 50%, transparent 100%)',
      zIndex: 1,
      opacity: {
        default: 0,
        ':hover': 1,
      },
      transitionProperty: 'opacity',
      transitionDuration: '0.3s',
      animationName: shimmerSweep,
      animationDuration: '0.8s',
      animationTimingFunction: 'ease-out',
      animationIterationCount: 'infinite',
    },
  },



  /* Call to Action Section */
  ctaSection: {
    backgroundImage: `linear-gradient(135deg, 
            var(--bg-card) 0%, 
            rgba(59, 130, 246, 0.02) 15%, 
            rgba(16, 185, 129, 0.02) 35%, 
            rgba(255, 107, 107, 0.02) 55%, 
            rgba(139, 92, 246, 0.02) 75%, 
            var(--bg-card) 100%)`,
    borderRadius: 'var(--radius-xl)',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'var(--border)',
    padding: 'var(--spacing-2xl)',
    marginBottom: 'var(--spacing-3xl)',
    textAlign: 'center',
    position: 'relative',
    overflow: 'hidden',
    backdropFilter: 'blur(4px)',
    '::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundImage: `
        radial-gradient(circle at 15% 40%, rgba(59, 130, 246, 0.03) 0%, transparent 50%),
        radial-gradient(circle at 85% 60%, rgba(16, 185, 129, 0.03) 0%, transparent 50%),
        radial-gradient(circle at 40% 20%, rgba(255, 107, 107, 0.02) 0%, transparent 50%),
        radial-gradient(circle at 60% 80%, rgba(139, 92, 246, 0.02) 0%, transparent 50%)`,
      opacity: 1,
    },
  },

  ctaContent: {
    position: 'relative',
    zIndex: 1,
  },

  ctaTitle: {
    fontSize: 'var(--font-size-2xl)',
    fontWeight: 600,
    color: 'var(--text-primary)',
    marginBottom: 'var(--spacing-md)',
  },

  ctaDescription: {
    fontSize: 'var(--font-size-lg)',
    color: 'var(--text-secondary)',
    marginBottom: 'var(--spacing-xl)',
    maxWidth: '500px',
    marginInline: 'auto',
  },

  ctaButtons: {
    display: 'flex',
    gap: 'var(--spacing-md)',
    justifyContent: 'center',
    flexWrap: 'wrap',
  },

  primaryButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    backgroundColor: 'var(--accent)',
    color: 'white',
    borderWidth: 0,
    paddingBlock: '0.875rem',
    paddingInline: '1.75rem',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: 600,
    textDecoration: 'none',
    position: 'relative',
    overflow: 'hidden',
    zIndex: 1,
    cursor: 'pointer',
    transitionProperty: 'all',
    transitionDuration: '0.3s',
    transform: {
      default: 'none',
      ':hover': 'translateY(-3px)',
    },
    boxShadow: {
      default: 'none',
      ':hover': '0 10px 20px rgba(0, 0, 0, 0.1)',
    },
    '::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: '-100%',
      width: '100%',
      height: '100%',
      backgroundImage: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
      zIndex: -1,
      animationName: shimmer,
      animationDuration: '3s',
      animationIterationCount: 'infinite',
    },
  },

  secondaryButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    backgroundColor: {
      default: 'transparent',
      ':hover': 'var(--bg-hover)',
    },
    color: 'var(--text-primary)',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: {
      default: 'var(--border)',
      ':hover': 'var(--border-hover)',
    },
    paddingBlock: '0.875rem',
    paddingInline: '1.75rem',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: 600,
    textDecoration: 'none',
    cursor: 'pointer',
    transitionProperty: 'all',
    transitionDuration: '0.3s',
    transform: {
      default: 'none',
      ':hover': 'translateY(-2px)',
    },
    boxShadow: {
      default: 'none',
      ':hover': '0 4px 8px rgba(0, 0, 0, 0.1)',
    },
  },

  /* Hero Mobile Overrides */
  hero: {
    flexDirection: {
      default: 'row',
      [MEDIA_768]: 'column',
    },
    textAlign: {
      default: 'left',
      [MEDIA_768]: 'center',
    },
    paddingTop: {
      default: 0,
      [MEDIA_768]: '2rem',
    },
    marginTop: {
      default: 0,
      [MEDIA_768]: 0,
    },
  },

  heroContent: {
    paddingRight: {
      default: '2rem',
      [MEDIA_768]: 0,
    },
    marginBottom: {
      default: 0,
      [MEDIA_768]: '2rem',
    },
  },

  heroSubtitle: {
    maxWidth: {
      default: '90%',
      [MEDIA_768]: '100%',
    },
    fontSize: {
      default: '1.25rem',
      [MEDIA_768]: '1.1rem',
      [MEDIA_480]: '1rem',
    },
  },

  heroTitle: {
    fontSize: {
      default: '3.5rem',
      [MEDIA_768]: '2rem',
      [MEDIA_480]: '1.75rem',
    },
  },

  heroStats: {
    gap: {
      default: 'var(--spacing-lg)',
      [MEDIA_480]: 'var(--spacing-md)',
    },
  },

  statItem: {
    padding: {
      default: 'var(--spacing-md)',
      [MEDIA_480]: 'var(--spacing-sm)',
    },
  },
});